import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import BellIcon from "@redotech/redo-icons/bell-03.svg";
import { TrackingPageTrackingUpdatesBlock } from "@redotech/redo-model/order-tracking/tracking-page-definition";
import { memo, useState } from "react";
import { toast } from "../../alert";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "../../arbiter-components/checkbox/redo-checkbox";
import { RedoTextInput } from "../../arbiter-components/input/redo-text-input";
import { ButtonSize } from "../../arbiter-components/redo-button-close-x";
import { Flex } from "../../flex";
import { Text } from "../../text";
import {
  TrackingPageContext,
  TrackingPageEnvironment,
} from "../context/tracking-page-context";
import {
  getBodyStyles,
  getButtonBorderRadius,
  getButtonHierarchy,
  getButtonStyles,
  getHeaderStyles,
  getLabelStyles,
} from "./shared/block-styles";
import { BlockWrapper } from "./shared/block-wrapper";

export const TrackingUpdates = memo(function TrackingUpdates({
  title,
  buttonSettings,
}: TrackingPageTrackingUpdatesBlock) {
  const context = useRequiredContext(TrackingPageContext);
  const { pageSettings, environment } = context;
  const trackingUpdates =
    environment === TrackingPageEnvironment.CUSTOMER_PORTAL
      ? context.trackingUpdates
      : undefined;

  const [phoneNumber, setPhoneNumber] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSignedUp, setIsSignedUp] = useState(false);

  const headerStyles = getHeaderStyles(pageSettings);
  const bodyStyles = getBodyStyles(pageSettings);
  const labelStyles = getLabelStyles(pageSettings);
  const buttonStyles = getButtonStyles(
    pageSettings?.pageButtonSettings,
    buttonSettings,
  );
  const buttonHierarchy = getButtonHierarchy(buttonSettings);
  const buttonBorderRadius = getButtonBorderRadius(pageSettings);

  const submit = useHandler(async () => {
    setIsSubmitting(true);
    const result = await trackingUpdates?.submit({
      phoneNumber,
      marketingConsent,
    });
    if (!result?.success) {
      toast(result?.error ?? "Failed to sign up for tracking updates", {
        variant: "error",
      });
    } else {
      setIsSignedUp(true);
      toast("Signed up for tracking updates", { variant: "success" });
    }
    setIsSubmitting(false);
  });

  return (
    <BlockWrapper
      environment={environment}
      loading={false}
      pageSettings={pageSettings}
    >
      <Flex dir="column" flex={1} gap="md" justify="space-between" p="3xl">
        <Flex align="center" dir="row" gap="md">
          <div style={headerStyles}>
            <BellIcon />
          </div>
          <Text fontSize="lg" fontWeight="semibold" style={headerStyles}>
            {title}
          </Text>
        </Flex>
        {isSignedUp ? (
          <Text fontSize="xs">
            You've been signed up for tracking updates. You'll receive text
            messages throughout the delivery process.
          </Text>
        ) : (
          <>
            <Flex align="center" dir="row" gap="md">
              <RedoTextInput
                fullWidth
                placeholder="Enter your phone number"
                placeholderStyle={labelStyles}
                setValue={setPhoneNumber}
                value={phoneNumber}
              />
              <RedoButton
                dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={{
                  ...buttonStyles,
                  ...buttonBorderRadius,
                }}
                hierarchy={buttonHierarchy}
                onClick={submit}
                pending={isSubmitting}
                size={ButtonSize.SMALL}
                text="Sign up"
              />
            </Flex>
            <RedoCheckbox
              label="Text me with news and offers"
              labelStyle={bodyStyles}
              setValue={(value) => setMarketingConsent(value === true)}
              size="xs"
              value={marketingConsent}
            />
          </>
        )}
      </Flex>
    </BlockWrapper>
  );
});
